import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X, House } from "@phosphor-icons/react";
import { FLAT_ROUTES } from "./Sidebar";
import { useAuth } from "@/context/AuthContext";
import { canAccessRoute, getDefaultRoute } from "@/lib/permissions";

const STORAGE_KEY = "labiferia:openTabs:v1";
const MAX_TABS = 10;

// Rutas que NO se agregan a las tabs (auth, público, home)
const IGNORED = ["/login", "/dashboard"];

function readTabs() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}
function writeTabs(tabs) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(tabs)); } catch { /* noop */ }
  window.dispatchEvent(new Event("tabschange"));
}

/**
 * Solapas de módulos abiertos en la parte superior. Cada tab persiste en localStorage
 * y muestra un botón "×" para cerrar. La tab activa se resalta.
 */
export default function ModuleTabs() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tabs, setTabs] = useState(readTabs);

  const isPublic = location.pathname.startsWith("/trazabilidad")
    || location.pathname === "/login";

  const home = user ? getDefaultRoute(user) : "/dashboard";
  const showHome = user && canAccessRoute(user, "/dashboard");

  // Filtrar tabs que el usuario actual NO puede ver (por cambio de permisos)
  useEffect(() => {
    if (!user) return;
    const current = readTabs();
    const allowed = current.filter((t) => canAccessRoute(user, t.path));
    if (allowed.length !== current.length) {
      writeTabs(allowed);
      setTabs(allowed);
    }
  }, [user]);

  // Agregar automáticamente la ruta actual como tab (si aplica)
  useEffect(() => {
    if (isPublic) return;
    const path = location.pathname;
    if (IGNORED.includes(path)) return;
    if (!FLAT_ROUTES[path]) return;
    if (user && !canAccessRoute(user, path)) return;
    const current = readTabs();
    if (current.some((t) => t.path === path)) return;
    const label = FLAT_ROUTES[path]?.label || path;
    const next = [...current, { path, label }].slice(-MAX_TABS);
    writeTabs(next);
    setTabs(next);
  }, [location.pathname, isPublic, user]);

  // Sincronizar cuando cambian las tabs (otras pestañas del navegador o cierre desde acá)
  useEffect(() => {
    const onChange = () => setTabs(readTabs());
    window.addEventListener("tabschange", onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener("tabschange", onChange);
      window.removeEventListener("storage", onChange);
    };
  }, []);

  const closeTab = useCallback((path, e) => {
    e.stopPropagation();
    e.preventDefault();
    const current = readTabs();
    const next = current.filter((t) => t.path !== path);
    writeTabs(next);
    setTabs(next);
    if (location.pathname === path) {
      const idx = current.findIndex((t) => t.path === path);
      const fallback = next[idx] || next[idx - 1] || next[0];
      navigate(fallback?.path || home);
    }
  }, [location.pathname, navigate, home]);

  const clearAll = useCallback(() => {
    writeTabs([]);
    setTabs([]);
    navigate(home);
  }, [navigate, home]);

  if (isPublic || tabs.length === 0) return null;

  return (
    <div className="border-b border-neutral-300 bg-white flex items-center px-2 h-10 flex-shrink-0 overflow-x-auto no-print">
      {showHome && (
        <>
          <button
            onClick={() => navigate("/dashboard")}
            title="Dashboard"
            className={`h-full px-3 flex items-center gap-1 border-b-2 transition-colors ${
              location.pathname === "/dashboard"
                ? "border-[#921A0E] text-[#921A0E]"
                : "border-transparent text-neutral-500 hover:text-neutral-800"
            }`}
            data-testid="module-tabs-home"
          >
            <House size={16} weight="duotone" />
          </button>
          <div className="w-px h-6 bg-neutral-200 mx-1" />
        </>
      )}
      {tabs.map((tab) => {
        const meta = FLAT_ROUTES[tab.path];
        const Icon = meta?.icon;
        const active = location.pathname === tab.path;
        return (
          <button
            key={tab.path}
            onClick={() => navigate(tab.path)}
            className={`group h-full px-3 flex items-center gap-2 border-b-2 transition-colors max-w-[220px] ${
              active
                ? "border-[#921A0E] text-[#921A0E] bg-[#921A0E]/5"
                : "border-transparent text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50"
            }`}
            data-testid={`module-tab-${tab.path.replace(/\//g, "-")}`}
            title={meta?.label || tab.label}
          >
            {Icon && <Icon size={14} weight={active ? "duotone" : "regular"} className="shrink-0" />}
            <span className="text-sm font-medium truncate">{meta?.label || tab.label}</span>
            <span
              onClick={(e) => closeTab(tab.path, e)}
              role="button"
              className={`shrink-0 w-5 h-5 rounded flex items-center justify-center transition-opacity ${
                active ? "opacity-70 hover:opacity-100 hover:bg-[#921A0E]/20" : "opacity-40 group-hover:opacity-90 hover:bg-neutral-200"
              }`}
              data-testid={`module-tab-close-${tab.path.replace(/\//g, "-")}`}
            >
              <X size={11} weight="bold" />
            </span>
          </button>
        );
      })}
      {tabs.length > 1 && (
        <button
          onClick={clearAll}
          className="ml-auto shrink-0 px-2 py-1 text-[11px] text-neutral-400 hover:text-rose-600 transition-colors"
          title="Cerrar todas las solapas"
          data-testid="module-tabs-clear"
        >
          Cerrar todas
        </button>
      )}
    </div>
  );
}
